import React, { useState } from 'react';
import { Check, X, ChevronDown, ChevronRight, AlertTriangle, Calendar, Trash2, Edit3, Flag } from 'lucide-react';

// ── ProposalCard ────────────────────────────────────────────────────────────
// Renders one Coach proposal inline in the chat stream with Confirm / Discard
// actions. The card is the user's confirmation gate before any state change —
// the AI never directly mutates Calendar or Plan; it proposes, the user
// confirms or dismisses, and only then does the app apply the change.
//
// Status states:
//   pending    — buttons shown, awaiting user
//   confirmed  — green banner, applied count
//   dismissed  — grey banner
//   stale      — amber banner, target no longer exists (e.g. workout was
//                manually deleted between proposal and confirm)
//
// Display vs protocol:
//   ProposalCard renders `proposal.summary` and a type-specific diff preview.
//   The raw JSON envelope from the AI is never shown to the user — it's
//   parsed out of the response and stripped from the displayed prose.

const TYPE_META = {
  add_workouts:     { icon: Calendar, label: 'Add to calendar',     accent: 'primary' },
  remove_workouts:  { icon: Trash2,   label: 'Remove from calendar', accent: 'danger'  },
  modify_phase:     { icon: Edit3,    label: 'Modify training phase', accent: 'accent-purple' },
  remove_phase:     { icon: Trash2,   label: 'Remove training phase', accent: 'danger' },
  add_milestone:    { icon: Flag,     label: 'Add milestone',         accent: 'success' },
  remove_milestone: { icon: Trash2,   label: 'Remove milestone',      accent: 'danger' },
  add_phase:        { icon: Calendar, label: 'Add training phase',    accent: 'primary' },
  set_plan:         { icon: Calendar, label: 'Replace season plan',   accent: 'warning' },
};

const ACCENT_CLASSES = {
  primary:        { border: 'border-primary-500/40',        iconBg: 'bg-primary-500/15',       iconText: 'text-primary-300' },
  danger:         { border: 'border-danger-500/40',         iconBg: 'bg-danger-500/15',        iconText: 'text-danger-300'  },
  success:        { border: 'border-success-500/40',        iconBg: 'bg-success-500/15',       iconText: 'text-success-300' },
  warning:        { border: 'border-warning-500/40',        iconBg: 'bg-warning-500/15',       iconText: 'text-warning-300' },
  'accent-purple': { border: 'border-accent-purple-500/40', iconBg: 'bg-accent-purple-500/15', iconText: 'text-accent-purple-300' },
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
};

// Per-type diff renderers — kept simple, surface the change at a glance
const DiffPreview = ({ proposal }) => {
  const { type, payload } = proposal;

  if (type === 'add_workouts') {
    return (
      <ul className="space-y-1.5 text-[13px]">
        {payload.items.map((w, i) => (
          <li key={i} className="flex items-start gap-2 text-fg-muted">
            <span className="font-medium text-fg whitespace-nowrap">{formatDate(w.date)}</span>
            <span className="text-fg-muted">·</span>
            <span className="flex-1">
              <span className="font-medium text-fg">{w.title}</span>
              <span className="text-fg-muted"> — {w.duration} min{w.zone ? ` ${w.zone}` : ''}</span>
              {w.description && <div className="text-fg-muted text-[12px] mt-0.5">{w.description}</div>}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  if (type === 'remove_workouts') {
    return (
      <div className="text-[13px] text-fg-muted">
        <div className="mb-1">{payload.workoutIds.length} workout(s) will be removed:</div>
        <ul className="space-y-0.5 font-mono text-[11px]">
          {payload.workoutIds.map((id) => (
            <li key={id} className="truncate">{id}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (type === 'modify_phase') {
    return (
      <div className="text-[13px] text-fg-muted">
        <div className="mb-1">Phase <span className="font-mono text-[11px]">{payload.phaseId}</span> changes:</div>
        <ul className="space-y-0.5">
          {Object.entries(payload.changes).map(([k, v]) => (
            <li key={k}>
              <span className="text-fg-muted">{k}: </span>
              <span className="text-fg font-medium">{String(v)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (type === 'remove_phase') {
    return (
      <div className="text-[13px] text-fg-muted">
        Phase <span className="font-mono text-[11px]">{payload.phaseId}</span> will be removed.
      </div>
    );
  }

  if (type === 'add_milestone') {
    const m = payload.milestone;
    return (
      <div className="text-[13px] text-fg-muted">
        <span className="font-medium text-fg">{formatDate(m.date)}</span>
        {' — '}
        <span className="text-fg">{m.label || m.name || m.type}</span>
        {m.type && m.label && <span className="text-fg-muted"> ({m.type})</span>}
      </div>
    );
  }

  if (type === 'remove_milestone') {
    return (
      <div className="text-[13px] text-fg-muted">
        Milestone <span className="font-mono text-[11px]">{payload.milestoneId}</span> will be removed.
      </div>
    );
  }

  if (type === 'add_phase' && payload.phase) {
    return (
      <div className="text-[13px] text-fg-muted">
        <span className="font-medium text-fg">{payload.phase.name}</span>
        {' — '}
        <span>{formatDate(payload.phase.startDate)} → {formatDate(payload.phase.endDate)}</span>
        {payload.phase.type && <span className="ml-1 text-fg-muted">({payload.phase.type})</span>}
      </div>
    );
  }

  if (type === 'set_plan') {
    return (
      <div className="text-[13px] text-fg-muted">
        {payload.phases.length} phase(s), {payload.milestones.length} milestone(s) — this will replace your current plan.
      </div>
    );
  }

  return null;
};

const ProposalCard = ({ proposal, onConfirm, onDiscard, compact = false }) => {
  const [expanded, setExpanded] = useState(!compact);
  const meta = TYPE_META[proposal.type] || { icon: Edit3, label: proposal.type, accent: 'primary' };
  const Icon = meta.icon;
  const accent = ACCENT_CLASSES[meta.accent] || ACCENT_CLASSES.primary;

  const status = proposal.status || 'pending';

  const StatusBanner = () => {
    if (status === 'confirmed') {
      const applied = proposal.applied || {};
      return (
        <div className="flex items-center gap-2 text-[12px] text-success-300 bg-success-500/10 border border-success-500/30 rounded-md px-2.5 py-1.5">
          <Check size={14} />
          <span>
            Applied
            {applied.count !== undefined && ` — ${applied.count} item${applied.count === 1 ? '' : 's'}`}
            {applied.skipped > 0 && `, ${applied.skipped} already existed`}
          </span>
        </div>
      );
    }
    if (status === 'dismissed') {
      return (
        <div className="flex items-center gap-2 text-[12px] text-fg-muted bg-surface-hover border border-border rounded-md px-2.5 py-1.5">
          <X size={14} />
          <span>Discarded</span>
        </div>
      );
    }
    if (status === 'stale') {
      return (
        <div className="flex items-center gap-2 text-[12px] text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-md px-2.5 py-1.5">
          <AlertTriangle size={14} />
          <span>{proposal.staleReason || 'No longer applicable'}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`mt-2 border ${accent.border} bg-surface-raised/50 rounded-lg p-2.5`}>
      <div className="flex items-start gap-2">
        <div className={`flex-shrink-0 w-7 h-7 rounded-md ${accent.iconBg} ${accent.iconText} flex items-center justify-center`}>
          <Icon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wide text-fg-muted font-medium">{meta.label}</span>
          </div>
          <div className="text-[14px] text-fg font-medium leading-snug mt-0.5">{proposal.summary?.replace(/\*\*/g, '').replace(/(?<!\S)\*(?!\S)/g, '')}</div>

          {/* Expandable diff */}
          {DiffPreview({ proposal }) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1.5 flex items-center gap-1 text-[11px] text-fg-muted hover:text-fg transition-colors"
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span>{expanded ? 'Hide details' : 'Show details'}</span>
            </button>
          )}
          {expanded && (
            <div className="mt-2 pl-1 border-l-2 border-border">
              <div className="pl-2"><DiffPreview proposal={proposal} /></div>
            </div>
          )}

          {/* Action buttons or status banner */}
          <div className="mt-2.5">
            {status === 'pending' ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onConfirm && onConfirm(proposal)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium bg-action text-fg-on-action rounded-md hover:bg-action-hover transition-colors"
                >
                  <Check size={13} />
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => onDiscard && onDiscard(proposal)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-fg-muted border border-border rounded-md hover:bg-surface-hover hover:text-fg transition-colors"
                >
                  <X size={13} />
                  Discard
                </button>
              </div>
            ) : (
              <StatusBanner />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Sibling warning banner shown above proposals when some were rejected
// during parsing (e.g. schema-invalid or capability-gated). Surfaces failures
// to the user instead of silently dropping them.
export const ProposalRejectionWarning = ({ count, parseError }) => {
  if (!parseError && (!count || count <= 0)) return null;
  return (
    <div className="mt-2 flex items-start gap-2 text-[12px] text-warning-300 bg-warning-500/10 border border-warning-500/30 rounded-md px-2.5 py-1.5">
      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
      <span>
        {parseError
          ? `I tried to propose a change but the action block was invalid (${parseError}). Please ask me to try again.`
          : `${count} proposed change${count === 1 ? '' : 's'} couldn't be shown (invalid format).`}
      </span>
    </div>
  );
};

export default ProposalCard;
